/**
 * Cart selection signifier
 * -----------------------------------------------------------------------------
 * Adds an "in cart / selection" signifier (a box with a check mark) on every
 * <product-card> whose product is currently part of the customer's selection.
 *
 * A product counts as selected when it appears in EITHER:
 *   1. the native storefront cart (/cart.js -> items[].handle), or
 *   2. the saved B2B Draft Order cart (App Proxy -> lineItems[].productHandle),
 *      when draft-order mode is enabled for the logged-in customer.
 *
 * Performance notes:
 *   - Bails out immediately (no network, no listeners) on pages that render no
 *     product cards, so static pages pay nothing.
 *   - Uses the theme's native `productlist:appended` event for load-more /
 *     infinite-scroll instead of a document-wide MutationObserver.
 *   - Reads cards from a live HTMLCollection and only writes to the DOM when a
 *     card's state actually changes.
 */
(() => {
  const b2bConfig = (window.themeVariables && window.themeVariables.b2b) || {};
  const draftCartEnabled = b2bConfig.draftCartEnabled === true;
  const proxyBase = b2bConfig.proxyBase || '/apps/b2b/cart';

  // Master switch: the signifier is a B2B feature. Only run for a logged-in
  // customer on a store with "Save cart as draft" enabled. Everyone else pays
  // nothing — no network, no listeners, no observers.
  if (!draftCartEnabled) return;

  const CARD_SELECTOR = 'product-card[handle]';
  const CHECK_SELECTOR = '.product-card__in-cart-check';

  // Card containers that hydrate their cards asynchronously after page load
  // without firing `productlist:appended` (they use replaceChildren under the
  // hood): recently-viewed grids, and the predictive-search drawer results.
  const LATE_CONTAINER_SELECTOR = 'recently-viewed-products, predictive-search';

  const hasCardsNow = document.querySelector(CARD_SELECTOR);
  const lateContainers = document.querySelectorAll(LATE_CONTAINER_SELECTOR);

  // Nothing to signify now and nothing that will inject cards later — do no work.
  if (!hasCardsNow && lateContainers.length === 0) return;

  // Live collection: reflects cards added later without re-querying the document.
  const cards = document.getElementsByTagName('product-card');

  // Handles tracked per source so either can update independently.
  const nativeHandles = new Set();
  const draftHandles = new Set();

  const storefrontRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const isSelected = (handle) =>
    !!handle && (nativeHandles.has(handle) || draftHandles.has(handle));

  const applyToCard = (card) => {
    if (!card.hasAttribute('handle')) return;
    const check = card.querySelector(CHECK_SELECTOR);
    if (!check) return;
    const selected = isSelected(card.getAttribute('handle'));
    // Only touch the DOM when the state actually changes.
    if (check.hidden === selected) check.hidden = !selected;
  };

  const applyAll = () => {
    for (let i = 0; i < cards.length; i++) applyToCard(cards[i]);
  };

  const replaceSet = (target, handles) => {
    target.clear();
    for (const handle of handles) {
      if (handle) target.add(handle);
    }
  };

  // --- Native storefront cart ------------------------------------------------

  const setNativeCart = (cart) => {
    replaceSet(nativeHandles, ((cart && cart.items) || []).map((item) => item && item.handle));
    applyAll();
  };

  const fetchNativeCart = async () => {
    try {
      const response = await fetch(`${storefrontRoot()}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      setNativeCart(await response.json());
    } catch (error) {
      /* Progressive enhancement — ignore failures. */
    }
  };

  // --- B2B Draft Order cart --------------------------------------------------

  const setDraftCart = (cart) => {
    replaceSet(
      draftHandles,
      ((cart && cart.lineItems) || []).map((line) => line && line.productHandle)
    );
    applyAll();
  };

  const fetchDraftCart = async () => {
    if (!draftCartEnabled) return;
    try {
      const response = await fetch(proxyBase, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) return;
      const data = await response.json().catch(() => ({}));
      setDraftCart(data && data.cart);
    } catch (error) {
      /* Progressive enhancement — ignore failures. */
    }
  };

  // --- Events ----------------------------------------------------------------

  // Native cart mutations carry the full cart object.
  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart) {
      setNativeCart(cart);
    } else {
      fetchNativeCart();
    }
  });
  document.addEventListener('cart:refresh', fetchNativeCart);

  // Draft-order save dispatches b2b:cart-saved with the saved cart.
  document.addEventListener('b2b:cart-saved', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart) {
      setDraftCart(cart);
    } else {
      fetchDraftCart();
    }
  });

  const applyToSubtree = (node) => {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.matches && node.matches(CARD_SELECTOR)) {
      applyToCard(node);
    } else if (node.querySelectorAll) {
      node.querySelectorAll(CARD_SELECTOR).forEach(applyToCard);
    }
  };

  // Load-more / infinite scroll: only touch the freshly appended cards.
  document.addEventListener('productlist:appended', (event) => {
    ((event.detail && event.detail.appended) || []).forEach(applyToSubtree);
  });

  // Async grids (e.g. recently-viewed) hydrate their cards after load with no
  // event. Watch only those specific containers — never the whole document.
  if (lateContainers.length > 0) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(applyToSubtree);
      }
    });
    lateContainers.forEach((container) =>
      observer.observe(container, { childList: true, subtree: true })
    );
  }

  // Seed the native cart from Liquid (cart.items rendered into themeVariables)
  // to skip a load-time /cart.js fetch. Cart mutations re-sync via the events
  // above. The draft cart has no Liquid equivalent, so it still fetches once.
  setNativeCart({ items: b2bConfig.initialCartItems || [] });
  fetchDraftCart();
})();
